import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import {
  CONVEY_INTEGRATION_FIELD_NAMES,
  ConveyWebErrors
} from "../../../../../../Pages/Integrations/types";
import { CONVEY_WEB_INPUT_LENGTH } from "../../../../../../shared";

const useConveyIntegrationsInputs = (
  errors: ConveyWebErrors,
  optionalPassword: boolean | undefined,
  setErrors: Dispatch<SetStateAction<ConveyWebErrors>>
) => {
  const { t } = useTranslation();

  const isValidBaseUrl = (url: string): boolean => {
    if (!url) return false;

    const trimmedUrl = url.trim();

    if (!/^https?:\/\/.+/i.test(trimmedUrl)) {
      return false;
    }

    try {
      const parsedUrl = new URL(trimmedUrl);

      const isValidProtocol =
        parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";

      const hasHostname = Boolean(parsedUrl.hostname);

      return isValidProtocol && hasHostname;
    } catch {
      return false;
    }
  };

  const isNotEmpty = (value: string, field: CONVEY_INTEGRATION_FIELD_NAMES) => {
    const fieldTranslation = t(`Dialog##${field}`);

    if (field === CONVEY_INTEGRATION_FIELD_NAMES.Password && optionalPassword) {
      return "";
    }

    return value?.trim()
      ? ""
      : t(`Dialog##field cannot be empty`, { fieldName: fieldTranslation });
  };

  const maxLength = (value: string, limit: number, msg: any) =>
    value?.trim().length > limit ? msg : "";

  // Single source of truth for each field's length rule, so both the
  // live (onChange) check and the blur check use the exact same limit/message.
  const validateLength = (
    field: CONVEY_INTEGRATION_FIELD_NAMES,
    value: string
  ): string => {
    switch (field) {
      case CONVEY_INTEGRATION_FIELD_NAMES.Name:
        return maxLength(
          value,
          CONVEY_WEB_INPUT_LENGTH.Name,
          t("Dialog##name not valid", {
            maxNumber: CONVEY_WEB_INPUT_LENGTH.Name
          })
        );

      case CONVEY_INTEGRATION_FIELD_NAMES.Username:
        return maxLength(
          value,
          CONVEY_WEB_INPUT_LENGTH.Username,
          t("Dialog##username cannot contain more than", {
            maxNumber: CONVEY_WEB_INPUT_LENGTH.Username
          })
        );

      case CONVEY_INTEGRATION_FIELD_NAMES.Password:
        return maxLength(
          value,
          CONVEY_WEB_INPUT_LENGTH.InputMaxLength,
          t("Dialog##password cannot contain more than", {
            maxNumber: CONVEY_WEB_INPUT_LENGTH.InputMaxLength
          })
        );

      default:
        return "";
    }
  };

  const validateEndpointUrl = (url: string) => {
    return !isValidBaseUrl(url) ? t("Dialog##endpoint url not valid") : "";
  };

  const validateField = (
    field: CONVEY_INTEGRATION_FIELD_NAMES,
    value: any
  ): string => {
    switch (field) {
      case CONVEY_INTEGRATION_FIELD_NAMES.Name:
      case CONVEY_INTEGRATION_FIELD_NAMES.Username:
      case CONVEY_INTEGRATION_FIELD_NAMES.Password:
        return isNotEmpty(value, field) || validateLength(field, value);

      case CONVEY_INTEGRATION_FIELD_NAMES.EndpointUrl:
        return isNotEmpty(value, field) || validateEndpointUrl(value);

      default:
        return "";
    }
  };

  // Runs on every keystroke. Only ever reflects the length rule for the
  // field being typed into: clears the error while the value is within
  // limit, shows the length message the moment it's exceeded. Any other
  // pending error (required/format) is intentionally cleared here too,
  // matching prior "clear error while typing" behavior — it gets
  // re-validated properly on blur.
  const handleChange = (field: CONVEY_INTEGRATION_FIELD_NAMES, value: any) => {
    const fieldAsErrorType = field as keyof ConveyWebErrors;
    const lengthError = validateLength(field, value);

    setErrors(prev => ({
      ...prev,
      [fieldAsErrorType]: lengthError
    }));
  };

  const handleBlur = (field: CONVEY_INTEGRATION_FIELD_NAMES, value: any) => {
    setErrors(prev => ({
      ...prev,
      [field]: validateField(field, value)
    }));
  };

  const clearFieldError = (field: keyof ConveyWebErrors) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return { handleChange, handleBlur, clearFieldError };
};

export default useConveyIntegrationsInputs;
